import React, { useState, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Icons } from "./Machine360Icons";
import Nodata from "../common-component/Nodata";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

const PERIOD_OPTIONS = [
  { value: 7, label: "Last 7 Days" },
  { value: 30, label: "Last 30 Days" },
  { value: 90, label: "Last 90 Days" },
];

export const UsageTrendChart = ({
  data,
  isLoading = false,
  selectedDays = 30,
  onPeriodChange,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState(selectedDays);

  useEffect(() => {
    setSelectedPeriod(selectedDays);
  }, [selectedDays]);

  const handlePeriodChange = (e) => {
    const val = Number(e.target.value);
    setSelectedPeriod(val);
    if (onPeriodChange) {
      onPeriodChange(val);
    }
  };

  if (isLoading) {
    return (
      <div className="m360-card m360-usage-trend-card">
        <div className="m360-card-header">
          <div className="m360-card-title-group">
            <Skeleton circle={true} height={16} width={16} />
            <Skeleton height={16} width={160} />
          </div>
          <div className="m360-card-actions">
            <Skeleton height={28} width={110} borderRadius={4} />
          </div>
        </div>

        <div
          className="m360-usage-chart-wrap d-flex align-items-end justify-content-between px-3"
          style={{ height: 210, paddingBottom: 16 }}
        >
          {[40, 75, 30, 95, 60, 45, 120, 65, 80, 50, 105, 70, 85, 40].map((h, i) => (
            <div
              key={i}
              className="d-flex flex-column align-items-center gap-1"
              style={{ width: 14 }}
            >
              <Skeleton height={h} width={8} borderRadius={2} />
            </div>
          ))}
        </div>

        <div className="m360-chart-bottom-legend">
          {[65, 55, 70].map((w, i) => (
            <span key={i} className="m360-chart-legend-item">
              <Skeleton circle={true} height={8} width={8} />
              <Skeleton height={12} width={w} />
            </span>
          ))}
        </div>
      </div>
    );
  }

  const title = data?.title || `Usage Trend (Last ${selectedPeriod} Days)`;
  const hasUsageData = Boolean(
    data?.series &&
    ((Array.isArray(data.series.successful) && data.series.successful.some((v) => Number(v) > 0)) ||
     (Array.isArray(data.series.failed) && data.series.failed.some((v) => Number(v) > 0)) ||
     (Array.isArray(data.series.inProcess) && data.series.inProcess.some((v) => Number(v) > 0)))
  );

  const chartData = {
    labels: data?.xAxisTickLabels || data?.labels || [],
    datasets: [
      {
        label: "Successful",
        data: data?.series?.successful || [],
        backgroundColor: "#2bb219",
        borderRadius: 0,
        stack: "usageStack",
        maxBarThickness: 16,
      },
      {
        label: "Failed",
        data: data?.series?.failed || [],
        backgroundColor: "#ee0000",
        borderRadius: 0,
        stack: "usageStack",
        maxBarThickness: 16,
      },
      {
        label: "InProcess",
        data: data?.series?.inProcess || [],
        backgroundColor: "#94a3b8",
        borderRadius: { topLeft: 2, topRight: 2 },
        stack: "usageStack",
        maxBarThickness: 16,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(17, 17, 17, 0.92)",
        padding: 8,
        cornerRadius: 4,
        titleFont: { family: "'Sora', sans-serif", size: 11 },
        bodyFont: { family: "'Sora', sans-serif", size: 11 },
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: "#94a3b8",
          font: { size: 10 },
          maxRotation: 0,
          autoSkip: false,
        },
      },
      y: {
        stacked: true,
        min: 0,
        suggestedMax: 8,
        ticks: {
          stepSize: 2,
          color: "#94a3b8",
          font: { size: 10 },
        },
        grid: {
          color: "#f1f5f9",
          drawBorder: false,
        },
        title: {
          display: true,
          text: "Runs",
          color: "#94a3b8",
          font: { size: 11 },
        },
      },
    },
  };

  return (
    <div className="m360-card m360-usage-trend-card">
      <div className="m360-card-header">
        <div className="m360-card-title-group">
          <Icons.Clock size={16} className="m360-card-header-icon" />
          <h4 className="m360-card-title">{title}</h4>
        </div>
        <div className="m360-card-actions">
          <select
            className="m360-select-dropdown"
            value={selectedPeriod}
            onChange={handlePeriodChange}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!hasUsageData ? (
        <div className="m360-card-nodata-wrapper">
          <Nodata />
        </div>
      ) : (
        <>
          <div className="m360-usage-chart-wrap">
            <Bar data={chartData} options={chartOptions} />
          </div>

          {/* Bottom Centered Legend */}
          <div className="m360-chart-bottom-legend">
            <span className="m360-chart-legend-item">
              <span
                className="m360-legend-dot"
                style={{ backgroundColor: "#2bb219" }}
              />
              <span>Successful</span>
            </span>
            <span className="m360-chart-legend-item">
              <span
                className="m360-legend-dot"
                style={{ backgroundColor: "#ee0000" }}
              />
              <span>Failed</span>
            </span>
            <span className="m360-chart-legend-item">
              <span
                className="m360-legend-dot"
                style={{ backgroundColor: "#94a3b8" }}
              />
              <span>InProcess</span>
            </span>
          </div>
        </>
      )}
    </div>
  );
};

